import { SITE } from "@/lib/site";

export const POLICIES = {
  privacy: {
    slug: "/privacy",
    title: "Privacy Policy",
    metaTitle: "Privacy Policy | Eli Capital Investments",
    metaDescription:
      "How Eli Capital Investments collects, uses and protects the information you submit through our wholesale inquiry form and website contact channels.",
    updated: "October 24, 2026",
    sections: [
      {
        h: "About this policy",
        paras: [
          `This Privacy Policy describes how ${SITE.name} ("we", "us", "our") handles information collected through ellicapitalinvestments.com and the contact channels listed on this site. We are a wholesale merchandise and import/export sourcing company based in Pembroke Pines, Florida. We do not operate a retail storefront and we do not sell goods directly to consumers through this website.`,
        ],
      },
      {
        h: "Information we collect",
        paras: [
          "When you submit an inquiry through our form, we collect the information you provide: your name, email address, phone number (optional), the product you are looking for, the quantity you need, the delivery destination, and any additional message you include.",
          "If you contact us by phone or email, we collect the details you choose to share with us in that conversation.",
          "Like most websites, our hosting platform may collect basic technical information such as IP address, browser type, and pages visited, to keep the site secure and functioning.",
        ],
      },
      {
        h: "How we use your information",
        paras: [
          "We use the information you submit to respond to your inquiry, prepare quotes, source and coordinate merchandise orders, arrange shipping, and maintain records of our business dealings. We may also use it to comply with applicable laws and to protect the security of our business.",
          "We do not sell, rent, or trade your personal information to anyone.",
        ],
      },
      {
        h: "When we share information",
        paras: [
          "To source and deliver your order, we may share necessary order details (for example, product, quantity, and delivery destination) with suppliers, carriers, freight forwarders, and customs brokers involved in fulfilling your order.",
          "We may share payment-related information with the payment processor we use for invoicing and card payments. We may also disclose information when required by law, regulation, or legal process.",
        ],
      },
      {
        h: "Cookies and analytics",
        paras: [
          "This website uses essential cookies and privacy-respecting analytics to understand overall site usage and improve the experience. No advertising trackers are used, and no information collected through this site is used for behavioral advertising.",
        ],
      },
      {
        h: "Data retention and security",
        paras: [
          "We keep inquiry and order records only as long as needed to respond to you, fulfill orders, maintain our business records, and meet legal and accounting obligations. We apply reasonable administrative and technical safeguards appropriate to the size and nature of our business.",
        ],
      },
      {
        h: "Your choices",
        paras: [
          "You may ask us to access, correct, or delete the personal information we hold about you, or ask us not to use it for a particular purpose. Contact us using the details below and we will respond within a reasonable time.",
        ],
      },
      {
        h: "Contact",
        paras: [
          `Eli Capital Investments, ${SITE.addressFull}. Phone: ${SITE.phoneDisplay}. Email: ${SITE.email}.`,
        ],
      },
      {
        h: "Changes to this policy",
        paras: [
          "We may update this policy from time to time. The effective date above reflects the most recent revision.",
        ],
      },
    ],
  },
  terms: {
    slug: "/terms-of-sale",
    title: "Terms of Sale",
    metaTitle: "Terms of Sale | Eli Capital Investments",
    metaDescription:
      "Wholesale terms of sale for Eli Capital Investments: quote-based pricing, order confirmation, payment, title, inspection and governing law.",
    updated: "October 24, 2026",
    sections: [
      {
        h: "About our business",
        paras: [
          `${SITE.name} is a wholesale merchandise and import/export sourcing company. We primarily source and coordinate merchandise orders for business buyers — including Amazon sellers, resellers and distributors, and buyers shipping merchandise overseas. We do not operate a warehouse or retail storefront, and any inventory we occasionally hold is limited to small quantities.`,
        ],
      },
      {
        h: "Quotes, availability and pricing",
        paras: [
          'All products, quantities and prices are provided by written quote. Because products and quantities vary, "call for availability and pricing" applies throughout this site: nothing on this website is an offer to sell at a stated price.',
          "An order exists only once it is confirmed by us in writing (including by email), stating the product, quantity, unit pricing, total, payment terms and delivery terms for that order.",
        ],
      },
      {
        h: "Payment",
        paras: [
          "Payment terms, including accepted payment methods and any deposit requirements, are stated on each confirmed order and invoice. Card payments, where accepted, are processed through our payment processor; we do not store full card numbers.",
          "Prices are quoted per order and are subject to product availability at the time of confirmation.",
        ],
      },
      {
        h: "Title, risk and delivery",
        paras: [
          "Title and risk of loss pass as stated on the confirmed order (for example, FOB origin or as otherwise agreed in writing). Delivery terms and shipping arrangements are described in our Shipping Policy and on each order confirmation.",
        ],
      },
      {
        h: "Inspection and acceptance",
        paras: [
          "Buyers should inspect merchandise on receipt. Any shortage, damage or discrepancy must be reported to us within five (5) business days of delivery, with supporting photographs where applicable, so we can address it under our Refund Policy or the terms of the order confirmation.",
        ],
      },
      {
        h: "Wholesale, commercial purchases",
        paras: [
          "Sales made through this site are wholesale transactions for resale or commercial use. Goods are sold subject to any condition descriptions agreed in the order confirmation. Descriptions of product categories on this website are general in nature.",
        ],
      },
      {
        h: "Limitation of liability",
        paras: [
          "To the fullest extent permitted by law, our liability arising from any order is limited to the amount paid for the merchandise giving rise to the claim. We are not liable for indirect or consequential losses, including lost profits or lost sales, to the extent permitted by law.",
        ],
      },
      {
        h: "Governing law",
        paras: [
          "These Terms of Sale and any order confirmed under them are governed by the laws of the State of Florida, without regard to conflict-of-law rules.",
        ],
      },
      {
        h: "Contact",
        paras: [
          `For questions about these terms, contact ${SITE.name}, ${SITE.addressFull}. Phone: ${SITE.phoneDisplay}. Email: ${SITE.email}.`,
        ],
      },
    ],
  },
  shipping: {
    slug: "/shipping",
    title: "Shipping Policy",
    metaTitle: "Shipping Policy | Eli Capital Investments",
    metaDescription:
      "How Eli Capital Investments arranges freight and delivery for wholesale orders: quotes by destination, lead times, international shipping and perishable goods.",
    updated: "October 24, 2026",
    sections: [
      {
        h: "Shipping is quoted per order",
        paras: [
          "Because products, quantities and destinations vary, shipping is quoted on each confirmed order. Your quote reflects the merchandise, its weight and volume, and the delivery destination.",
        ],
      },
      {
        h: "Carriers and freight",
        paras: [
          "We coordinate the movement of merchandise through the freight and parcel partners appropriate to the order — including parcel carriers, less-than-truckload (LTL) and full-truckload (FTL) freight, and ocean or air freight for international destinations. Shipping costs, routing and responsibilities are stated on the order confirmation.",
        ],
      },
      {
        h: "Lead times",
        paras: [
          "Lead times vary by product, supplier location and destination. Estimated timing is provided on your quote or order confirmation and is not a guarantee, as schedules depend on carriers and suppliers outside our control.",
        ],
      },
      {
        h: "International orders",
        paras: [
          "For orders shipping overseas, the buyer is responsible for compliance with the import laws, duties, taxes and clearance requirements of the destination country, unless otherwise agreed in writing. We provide standard commercial documents agreed at order confirmation — such as the commercial invoice and packing list — to support the shipment.",
        ],
      },
      {
        h: "Perishable goods",
        paras: [
          "Perishable merchandise is scheduled to minimize transit time, and handling conditions (such as packaging and temperature considerations) are agreed on the order confirmation. Buyers should be ready to receive perishable shipments promptly on arrival.",
        ],
      },
      {
        h: "Delivery addresses and receipt",
        paras: [
          "Please verify the delivery address, contact person and any receiving restrictions (such as liftgate or appointment requirements) before your order is confirmed. If a delivery problem occurs, contact us as soon as possible so we can work with the carrier on your behalf.",
        ],
      },
      {
        h: "Contact",
        paras: [
          `Questions about a shipment? Call ${SITE.phoneDisplay} or email ${SITE.email}. ${SITE.name}, ${SITE.addressFull}.`,
        ],
      },
    ],
  },
  refund: {
    slug: "/refund",
    title: "Refund Policy",
    metaTitle: "Refund Policy | Eli Capital Investments",
    metaDescription:
      "Refund and claims terms for wholesale orders from Eli Capital Investments: final-sale basis, reporting shortages or damage, cancellations and remedy process.",
    updated: "October 24, 2026",
    sections: [
      {
        h: "Wholesale sales are final",
        paras: [
          "Unless the order confirmation states otherwise, merchandise sold by ${name} is sold on a final-sale basis once the order is confirmed.".replace(
            "${name}",
            SITE.name
          ),
        ],
      },
      {
        h: "Reporting shortages, damage or discrepancies",
        paras: [
          "If merchandise received does not match the confirmed order — including shortages, damage in transit, or discrepancies — report it to us within five (5) business days of delivery, with photographs where applicable. Reports should include your name and reference or invoice number.",
          "Where a claim is accepted, we will remedy it as stated on the order confirmation, which may include a replacement, a credit toward a future order, or a refund, at our reasonable discretion and consistent with the carrier's claims process.",
        ],
      },
      {
        h: "Cancellations",
        paras: [
          "Orders may be cancelled before shipment by contacting us. If sourcing or supplier costs have already been incurred for a cancelled order, those costs may be deducted from any refund, as stated in the order confirmation.",
        ],
      },
      {
        h: "Perishable goods",
        paras: [
          "Perishable merchandise is handled on tight timelines and under conditions agreed on the order confirmation. Any issue with a perishable delivery should be reported immediately upon receipt so it can be addressed with the supplier and carrier.",
        ],
      },
      {
        h: "Refund method and timing",
        paras: [
          "Where a refund applies, it is issued to the original payment method used for the order. Refunds are processed within a reasonable time after the claim is accepted and any required documentation is complete.",
        ],
      },
      {
        h: "How to request",
        paras: [
          `Call ${SITE.phoneDisplay} or email ${SITE.email} with your name and reference or invoice number. ${SITE.name}, ${SITE.addressFull}.`,
        ],
      },
    ],
  },
};
